import React from "react";

const DURATION_FACTOR_OPTIONS = [
  0.3,
  0.4,
  0.5,
  0.75,
  1,
  1.25,
  1.5,
  2,
  2.5,
  3,
];

function cx(...classes) {
  return classes.filter(Boolean).join(" ");
}

function formatSpeed(speed) {
  if (speed === 1) return "100%";
  if (speed === 0.5) return "50%";
  if (speed === 0.25) return "25%";
  return `${Math.round(Number(speed) * 100)}%`;
}

export default function BetterMeControlPanel({
  hintText,

  durationFactor,
  onDurationFactorChange,
  onAddDurationRegion,

  localSpeed,
  onSpeedChange,

  playing,
  canInteract,
  canPlay,
  loading,
  savingBetterMe,

  sourceAudioUrl,
  workingAudioUrl,
  previewAudioId,
  handlesCount,
  hasUnsavedTarget,

  onPlayOriginal,
  onPlayEdited,
  onTryPreview,
  onSaveBetterMe,
}) {
  const canMarkDuration =
    canInteract &&
    !loading &&
    !savingBetterMe &&
    Number(durationFactor) !== 1;

  const canPlayOriginal =
    canPlay &&
    !loading &&
    !savingBetterMe &&
    Boolean(sourceAudioUrl);

  const canPlayEdited =
    canPlay &&
    !loading &&
    !savingBetterMe &&
    Boolean(workingAudioUrl);

  const canTryBetterMe =
    canInteract &&
    !loading &&
    !savingBetterMe &&
    Number(handlesCount) >= 2;

  const canSaveBetterMe =
    canInteract &&
    !loading &&
    !savingBetterMe &&
    Boolean(previewAudioId);

  return (
    <div className="mt-4 rounded-[26px] border border-violet-100 bg-white p-4 shadow-sm">
      <div className="grid gap-4">
        <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-bold leading-6 text-slate-700">
          {hintText}
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6">
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-3 shadow-sm">
            <div className="flex items-center gap-2">
              <span className="shrink-0 text-[10px] font-black uppercase tracking-[0.14em] text-amber-700">
                Duration A–B
              </span>

              <select
                value={durationFactor}
                onChange={(event) =>
                  onDurationFactorChange(Number(event.target.value))
                }
                className="min-w-0 flex-1 rounded-xl border border-amber-200 bg-white px-3 py-2 text-xs font-black text-amber-900 outline-none focus:ring-4 focus:ring-amber-100"
              >
                {DURATION_FACTOR_OPTIONS.map((factor) => (
                  <option key={factor} value={factor}>
                    {factor}×
                  </option>
                ))}
              </select>
            </div>

            <button
              type="button"
              onClick={onAddDurationRegion}
              disabled={!canMarkDuration}
              className="mt-2 w-full rounded-xl border border-amber-500 bg-amber-400 px-3 py-2 text-xs font-black text-amber-950 shadow-sm transition hover:-translate-y-0.5 hover:bg-amber-300 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Mark stretch
            </button>

            <div className="mt-2 text-[11px] font-bold leading-4 text-amber-800">
              Use A/B lines to choose the sound. 0.3× compresses, 3× stretches.
              Nested regions stack.
            </div>
          </div>

          <div className="min-h-[104px] rounded-2xl border border-slate-200 bg-slate-50 p-3 shadow-sm">
            <div className="mb-3 flex items-center justify-between gap-2">
              <span className="text-[10px] font-black uppercase tracking-[0.14em] text-slate-400">
                Speed
              </span>

              <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[10px] font-black text-slate-500">
                {formatSpeed(localSpeed)}
              </span>
            </div>

            <div className="grid grid-cols-3 gap-1 rounded-2xl bg-white p-1.5 shadow-inner">
              {[
                { value: 0.25, label: "25" },
                { value: 0.5, label: "50" },
                { value: 1, label: "100" },
              ].map((speed) => {
                const active = localSpeed === speed.value;

                return (
                  <button
                    key={speed.value}
                    type="button"
                    onClick={() => onSpeedChange(speed.value)}
                    className={cx(
                      "min-h-[54px] rounded-xl text-sm font-black transition focus:outline-none focus:ring-4 focus:ring-blue-100",
                      active
                        ? "bg-blue-600 text-white shadow-md shadow-blue-500/20"
                        : "bg-slate-50 text-slate-600 hover:bg-blue-50 hover:text-blue-700"
                    )}
                  >
                    {speed.label}
                  </button>
                );
              })}
            </div>
          </div>

          <button
            type="button"
            onClick={onPlayOriginal}
            disabled={!canPlayOriginal}
            className={cx(
              "min-h-[104px] rounded-2xl border px-4 py-3 text-sm font-black shadow-sm transition hover:-translate-y-0.5 disabled:opacity-60",
              playing === "original"
                ? "border-blue-700 bg-blue-600 text-white"
                : "border-blue-200 bg-white text-blue-800 hover:border-blue-300"
            )}
          >
            {playing === "original" ? "■ Stop original" : "▶ Play original"}
          </button>

          <button
            type="button"
            onClick={onPlayEdited}
            disabled={!canPlayEdited}
            className={cx(
              "min-h-[104px] rounded-2xl border px-4 py-3 text-sm font-black shadow-sm transition hover:-translate-y-0.5 disabled:opacity-50",
              playing === "edited"
                ? "border-violet-700 bg-violet-600 text-white"
                : "border-violet-300 bg-white text-violet-800 hover:border-violet-400"
            )}
            title={
              workingAudioUrl
                ? "Play latest Better Me audio"
                : "Create Better Me first"
            }
          >
            {playing === "edited" ? "■ Stop edited" : "▶ Play edited"}
          </button>

          <button
            type="button"
            onClick={onTryPreview}
            disabled={!canTryBetterMe}
            className="min-h-[104px] rounded-2xl px-5 py-3 text-sm font-black shadow-lg transition hover:-translate-y-0.5 active:translate-y-0 disabled:cursor-not-allowed disabled:opacity-60"
            style={{
              backgroundColor: "#6d28d9",
              color: "#ffffff",
              border: "2px solid #4c1d95",
              boxShadow: "0 14px 30px rgba(109, 40, 217, 0.32)",
            }}
          >
            {loading
              ? "Creating…"
              : workingAudioUrl && hasUnsavedTarget
                ? "✦ Try updated Better Me"
                : "✦ Try Better Me"}
          </button>

          <button
            type="button"
            onClick={onSaveBetterMe}
            disabled={!canSaveBetterMe}
            className="min-h-[104px] rounded-2xl px-5 py-3 text-sm font-black shadow-lg transition hover:-translate-y-0.5 active:translate-y-0 disabled:cursor-not-allowed disabled:opacity-50"
            style={{
              backgroundColor: previewAudioId ? "#059669" : "#e2e8f0",
              color: previewAudioId ? "#ffffff" : "#64748b",
              border: previewAudioId
                ? "2px solid #047857"
                : "2px solid #cbd5e1",
              boxShadow: previewAudioId
                ? "0 14px 30px rgba(5, 150, 105, 0.28)"
                : "none",
            }}
          >
            {savingBetterMe ? "Saving…" : "✓ Save Better Me"}
          </button>
        </div>
      </div>
    </div>
  );
}